/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  CheckCircleOutline,
  FlagOutlined,
  HelpOutline,
} from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import {
  UltraFlagDetectionImageMutation,
  UltraFlagDetectionImageMutationVariables,
  UltraFlaggedTypeChoices,
} from '@dai/graphql'
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { DetectionImageSession, FLAG_DETECTION_IMAGE } from '@/graphql'
import useT from '@/i18n/useT'
import { usePageHeaderActions } from '@/layouts/PageHeaderActionsContext'

import DetectionAnnotator, {
  DetectionAnnotatorHandle,
} from './DetectionAnnotator'
import DetectionHelpDialog from './DetectionHelpDialog'
import DetectionSelectionControls from './DetectionSelectionControls'
import DetectionSidebar from './DetectionSidebar'
import FlagItemModal from './FlagItemModal'
import { getOperationErrorMessage } from './detectionUtils'
import type { ReviewDetectionLocationState } from './review/reviewNavigation'
import ReviewFeedbackNotice from './review/ReviewFeedbackNotice'
import { useDetectionWorkflow } from './useDetectionWorkflow'

type DetectionProps = {
  initialSession?: DetectionImageSession | null
  hideInternalNavigation?: boolean
  externalUIHeight?: number
  onApproveSuccess?: () => Promise<unknown> | unknown
  reviewSidebarContent?: React.ReactNode
}

function Detection({
  initialSession: initialSessionProp = null,
  hideInternalNavigation = false,
  externalUIHeight = 0,
  onApproveSuccess,
  reviewSidebarContent = null,
}: DetectionProps) {
  const { t } = useT()
  const location = useLocation()
  const locationState = location.state as ReviewDetectionLocationState | null
  const initialSession =
    initialSessionProp ?? locationState?.reviewDetectionSession ?? null
  const annotatorRef = React.useRef<DetectionAnnotatorHandle | null>(null)
  const annotatorHeaderObserverRef = React.useRef<ResizeObserver | null>(null)
  const [helpOpen, setHelpOpen] = React.useState(false)
  const [flagModalOpen, setFlagModalOpen] = React.useState(false)
  const [annotatorHeaderHeight, setAnnotatorHeaderHeight] = React.useState(0)

  const annotatorHeaderRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      annotatorHeaderObserverRef.current?.disconnect()
      annotatorHeaderObserverRef.current = null
      if (!element) {
        setAnnotatorHeaderHeight(0)
        return
      }

      const updateHeight = () => {
        setAnnotatorHeaderHeight(
          Math.ceil(element.getBoundingClientRect().height),
        )
      }

      updateHeight()
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(updateHeight)
        observer.observe(element)
        annotatorHeaderObserverRef.current = observer
      }
    },
    [],
  )

  const {
    activeClass,
    activeTag,
    addingTag,
    boxes,
    canApplyActiveTag,
    canApplyCurrentLabel,
    canEdit,
    canUseSearchAsTag,
    counts,
    error,
    filteredTags,
    handleFlaggedImage,
    handleApplyActiveTag,
    handleApplyCurrentLabel,
    handleApprove,
    handleCreateBox,
    handleCreateTag,
    handleDeleteSelectedBox,
    handleNextImage,
    handleReload,
    handleSelectBoxesForClass,
    handleToggleClassVisibility,
    hiddenClasses,
    hoveredClass,
    image,
    leaseLock,
    loading,
    loadingImage,
    loadingNext,
    noItems,
    returnedFeedbackCount,
    reviewFeedback,
    removingBoxes,
    searchingTags,
    selectedBoxIds,
    selectedBoxes,
    setActiveClass,
    setActiveTag,
    setHoveredClass,
    setSelectedBoxIds,
    setSnackbarMessage,
    setTagSearch,
    showActiveTagSection,
    snackbarMessage,
    statusMessage,
    tagSearch,
    verifying,
  } = useDetectionWorkflow(initialSession, { onApproveSuccess })

  const selectedCounts = React.useMemo(() => {
    const selectedIds = new Set(selectedBoxIds)
    return boxes.reduce(
      (acc, box) => {
        if (selectedIds.has(box.id)) {
          acc[box.detectionClass] += 1
        }
        return acc
      },
      {
        PRODUCT: 0,
        PRICE_TAG: 0,
        EMPTY_FACING: 0,
      },
    )
  }, [boxes, selectedBoxIds])

  const [flagDetectionImage, { loading: flaggingImage }] = useMutation<
    UltraFlagDetectionImageMutation,
    UltraFlagDetectionImageMutationVariables
  >(FLAG_DETECTION_IMAGE, {
    context: { endPoint: 'mlUltra' },
  })

  const allProductBoxesTagged = boxes
    .filter(box => box.detectionClass === 'PRODUCT')
    .every(box => Boolean(box.tagId))
  const statusMessageSeverity = canEdit ? 'info' : 'warning'
  const showStatusMessageInSidebar = hideInternalNavigation && statusMessage
  const contentMaxHeight = hideInternalNavigation
    ? `calc(100dvh - ${190 + externalUIHeight + annotatorHeaderHeight}px)`
    : `calc(100dvh - ${145 + annotatorHeaderHeight}px)`
  const annotatorMaxImageHeight = hideInternalNavigation
    ? `calc(100dvh - ${135 + externalUIHeight + annotatorHeaderHeight}px)`
    : `calc(100dvh - ${115 + annotatorHeaderHeight}px)`

  const retryAfterLabel = (retryAfterMs: number | null) =>
    typeof retryAfterMs === 'number'
      ? `${Math.ceil(retryAfterMs / 1000)}s`
      : t('detection.retryAfter.moment', 'a moment')

  usePageHeaderActions(
    <Tooltip title={t('detection.help.tooltip', 'How detection works')}>
      <IconButton
        size="small"
        onClick={() => setHelpOpen(true)}
        aria-label={t('detection.help.ariaLabel', 'Open detection help')}
      >
        <HelpOutline fontSize="small" />
      </IconButton>
    </Tooltip>,
  )

  return (
    <>
      <Helmet title={t('detection.helmet', 'Detection')} />
      {loading ? (
        <Alert severity="info">
          {t('detection.loadingImage', 'Loading next detection image...')}
        </Alert>
      ) : error ? (
        <Alert severity="error">
          {t('detection.loadError', 'Failed to load detection image.')}
        </Alert>
      ) : leaseLock ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleReload}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('detection.leaseLock.message', {
            message: leaseLock.message,
            retryAfter: retryAfterLabel(leaseLock.retryAfterMs),
            defaultValue: `${leaseLock.message} Try again in ${retryAfterLabel(leaseLock.retryAfterMs)}.`,
          })}
        </Alert>
      ) : noItems ? (
        <Alert severity="info">{noItems.message}</Alert>
      ) : !image ? (
        <Alert severity="info">
          {t('detection.noImage', 'No detection image available.')}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', lg: '348px minmax(0, 1fr)' },
              alignItems: { xs: 'start', lg: 'stretch' },
              minHeight: 0,
              height: { lg: contentMaxHeight },
            }}
          >
            <DetectionSidebar
              imageId={image.id}
              sidebarNotice={
                showStatusMessageInSidebar ? statusMessage : undefined
              }
              sidebarNoticeSeverity={statusMessageSeverity}
              sidebarFooter={reviewSidebarContent}
              activeClass={activeClass}
              activeTag={activeTag}
              filteredTags={filteredTags}
              hiddenClasses={hiddenClasses}
              counts={counts}
              selectedCounts={selectedCounts}
              showActiveTagSection={showActiveTagSection}
              canUseSearchAsTag={canUseSearchAsTag}
              tagSearch={tagSearch}
              addingTag={addingTag}
              searchingTags={searchingTags}
              loadingNext={loadingNext}
              loadingImage={loadingImage}
              showNextImageAction={!hideInternalNavigation}
              onNextImage={handleNextImage}
              onActiveClassChange={setActiveClass}
              onHoveredClassChange={setHoveredClass}
              onSelectBoxesForClass={handleSelectBoxesForClass}
              onToggleClassVisibility={handleToggleClassVisibility}
              onTagSearchChange={setTagSearch}
              onCreateTag={handleCreateTag}
              onActiveTagChange={setActiveTag}
            />

            <Box
              sx={{
                width: '100%',
                minHeight: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  width: '100%',
                  minHeight: 0,
                  alignItems: 'center',
                }}
              >
                <Stack
                  ref={annotatorHeaderRef}
                  spacing={1}
                  sx={{ width: '100%' }}
                >
                  {statusMessage && !showStatusMessageInSidebar ? (
                    <Alert
                      severity={statusMessageSeverity}
                      sx={{ width: '100%' }}
                    >
                      {statusMessage}
                    </Alert>
                  ) : null}
                  <ReviewFeedbackNotice
                    feedback={reviewFeedback}
                    returnedCount={returnedFeedbackCount}
                  />
                  {image.isFlagged ? (
                    <Alert severity="warning" sx={{ width: '100%' }}>
                      {t(
                        'detection.flaggedImageNotice',
                        'This image is flagged.',
                      )}
                    </Alert>
                  ) : null}
                </Stack>
                <DetectionAnnotator
                  ref={annotatorRef}
                  imageUrl={image.imageUrl}
                  boxes={boxes}
                  editable={canEdit}
                  activeClass={activeClass}
                  activeTag={activeTag}
                  selectedBoxIds={selectedBoxIds}
                  hoveredClass={hoveredClass}
                  hiddenClasses={hiddenClasses}
                  maxImageHeight={annotatorMaxImageHeight}
                  onCreateBox={handleCreateBox}
                  onSelectedBoxIdsChange={setSelectedBoxIds}
                  bottomLeftControls={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <DetectionSelectionControls
                        boxes={boxes}
                        selectedBoxes={selectedBoxes}
                        canEdit={canEdit}
                        removingBoxes={removingBoxes}
                        canApplyCurrentLabel={canApplyCurrentLabel}
                        canApplyActiveTag={canApplyActiveTag}
                        activeClass={activeClass}
                        activeTag={activeTag}
                        onApplyCurrentLabel={handleApplyCurrentLabel}
                        onApplyActiveTag={handleApplyActiveTag}
                        onDeleteSelected={handleDeleteSelectedBox}
                        onSelectedBoxIdsChange={setSelectedBoxIds}
                      />
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => annotatorRef.current?.resetView()}
                      >
                        {t('detection.resetView', 'Reset view')}
                      </Button>
                    </Stack>
                  }
                  bottomRightControls={
                    <Stack
                      direction="row"
                      spacing={1.5}
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlined"
                        startIcon={<FlagOutlined />}
                        onClick={() => setFlagModalOpen(true)}
                        disabled={
                          !image || !canEdit || image.isFlagged || flaggingImage
                        }
                      >
                        {t('detection.flagImage', 'Flag image')}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CheckCircleOutline />}
                        disabled={
                          !allProductBoxesTagged || verifying || !canEdit
                        }
                        onClick={handleApprove}
                      >
                        {t('detection.approve', 'Approve')}
                      </Button>
                    </Stack>
                  }
                />
              </Stack>
            </Box>
          </Box>
        </Stack>
      )}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={5000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
      <FlagItemModal
        open={flagModalOpen}
        onClose={() => setFlagModalOpen(false)}
        flagType={UltraFlaggedTypeChoices.DETECTION_IMAGE}
        title={t('detection.flagModal.title', 'Flag Detection Image')}
        subjectLabel={t('detection.flagModal.subjectLabel', 'detection image')}
        onSubmit={async ({ reasonOptionId, moreInfo }) => {
          if (!image) return

          const response = await flagDetectionImage({
            variables: {
              input: {
                imageId: image.id,
                reasonOptionId,
                moreInfo: moreInfo ?? null,
              },
            },
          })
          const payload = response.data?.flagDetectionImage

          if (payload?.__typename === 'OperationInfo') {
            throw new Error(getOperationErrorMessage(t, payload.messages))
          }

          if (
            payload?.__typename !== 'FlagDetectionImageResult' ||
            !payload.image?.isFlagged
          ) {
            throw new Error(
              t(
                'detection.flagModal.failure',
                'Flag detection image did not succeed',
              ),
            )
          }

          setFlagModalOpen(false)
          setSnackbarMessage(
            t('detection.flaggedImageToast', {
              imageId: image.id.slice(0, 8),
              defaultValue: `Flagged image ${image.id.slice(0, 8)}`,
            }),
          )
          await handleFlaggedImage()
        }}
      />
      <DetectionHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  )
}

export default Detection
